import React, { useState } from 'react';
import axios from 'axios';
import { KeenIcon } from '@/components';
import clsx from "clsx";
import {enqueueSnackbar} from "notistack";

interface IValuesProps {
    title: string;
    items: string[];
    className?: string;
    company_p_id: string; // Assuming companyId is needed for backend updates
}

const Values = ({ title, items, className, company_p_id }: IValuesProps) => {
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [editedItems, setEditedItems] = useState<string[]>([...items]);
    const [newResponsibility, setNewResponsibility] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);

    const API_URL = import.meta.env.VITE_APP_API_URL;

    const handleEditClick = () => setIsEditing(true);

    const handleCancelClick = () => {
        setEditedItems([...items]);
        setNewResponsibility('');
        setIsEditing(false);
    };

    const handleSaveClick = async () => {
        setLoading(true);

        try {
            const token = localStorage.getItem('authToken'); // Adjust token retrieval as needed
            await axios.put(
                `${API_URL}/company/${company_p_id}/values`,
                { values: editedItems },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                }
            );
            setIsEditing(false);
            // Optionally, trigger a parent component update or refresh items here
        } catch (err) {
            enqueueSnackbar(`Failed to update values: ${err}`, {
                variant: 'solid',
                state: 'danger'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleAddResponsibility = () => {
        const trimmedResponsibility = newResponsibility.trim();
        if (trimmedResponsibility && !editedItems.includes(trimmedResponsibility)) {
            setEditedItems([...editedItems, trimmedResponsibility]);
            setNewResponsibility('');
        }
    };

    const handleEditResponsibility = (value: string, index: number) => {
        const updatedItems = [...editedItems];
        updatedItems[index] = value;
        setEditedItems(updatedItems);
    };

    const handleDeleteResponsibility = (index: number) => {
        const updatedItems = editedItems.filter((_, i) => i !== index);
        setEditedItems(updatedItems);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setNewResponsibility(e.target.value);
    };

    const renderItemView = (item: string, index: number) => (
        <div key={index} className="flex items-center gap-2.5 w-full">
            <span>
                <KeenIcon icon="like" className="text-sm text-gray-500" />
            </span>
            <span className="flex-1 text-sm">{item}</span>
        </div>
    );

    const renderItemEdit = (item: string, index: number) => (
        <div key={index} className="flex items-center gap-2.5 w-full mb-2">
            <span>
                <KeenIcon icon="like" className="text-sm text-gray-500" />
            </span>
            <input
                type="text"
                value={item}
                onChange={(e) => handleEditResponsibility(e.target.value, index)}
                className="input input-bordered input-sm flex-1"
                placeholder="Edit responsibility"
            />
            <button
                type="button"
                className="btn btn-ghost btn-sm text-red-500"
                onClick={() => handleDeleteResponsibility(index)}
                aria-label={`Delete ${item}`}
            >
                &times;
            </button>
        </div>
    );

    return (
        <div className={clsx('card', className && className)}>
            <div className="card-header flex justify-between items-center">
                <h3 className="card-title">{title}</h3>
                {!isEditing ? (
                    <button className="btn btn-light btn-sm" onClick={handleEditClick}>
                        <KeenIcon icon="notepad-edit" />
                        Edit
                    </button>
                ) : (
                    <div className="flex space-x-2">
                        <button className="btn btn-primary btn-sm" onClick={handleSaveClick} disabled={loading}>
                            <KeenIcon icon="file-up" />
                            {loading ? 'Saving...' : 'Save'}
                        </button>
                        <button className="btn btn-secondary btn-sm" onClick={handleCancelClick} disabled={loading}>
                            <KeenIcon icon="tablet-delete" />
                            Cancel
                        </button>
                    </div>
                )}
            </div>

            <div className="card-body">

                <div className={`flex flex-wrap gap-2.5 mb-4 ${isEditing ? 'flex-col' : ''}`}>
                    {isEditing ? (
                        editedItems.map((item, index) => renderItemEdit(item, index))
                    ) : (
                        editedItems.map((item, index) => renderItemView(item, index))
                    )}
                </div>

                {isEditing && (
                    <div className="flex items-center space-x-2">
                        <input
                            type="text"
                            value={newResponsibility}
                            onChange={handleInputChange}
                            placeholder="Add new responsibility"
                            className="input input-bordered input-sm flex-1"
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleAddResponsibility();
                                }
                            }}
                        />
                        <button
                            type="button"
                            className="btn btn-success btn-sm"
                            onClick={handleAddResponsibility}
                            disabled={!newResponsibility.trim()}
                        >
                            <KeenIcon icon="add-notepad" />
                            Add
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export { Values, type IValuesProps };
